import { useEffect, useRef, useState } from 'react';
import { Chip, Chips, SearchInput } from '../../components/shared';
import { searchUsers, type UserSummary } from '../../api/users';

/**
 * 결재자·참고자 **사람 고르기** — 근태 5단계 (2026-08-24).
 *
 * 설계: `docs/attendance-round2-sample.html` 화면 M · 현황("결재자를 아이디로 적어 넣는다").
 *
 *   기안 화면의 결재선 입력이 **사용자 ID 를 콤마로 나열하는 텍스트 칸**이었다(`approverIdsCsv`).
 *   사람을 고르는 UI 가 아예 없어서, 결재자를 넣으려면 남의 숫자 id 를 어딘가에서 알아 와야 했다.
 *
 * ★ 조직도 팝업과 자동완성은 **이미 있다** — 메일 받는사람 고르기(`RecipientPicker`, 200ms
 *   디바운스 + `GET /users?search=`)와 같은 방식을 그대로 옮긴다. 새로 만들지 않는다.
 * ★ 순서가 뜻을 갖는다(결재는 1단계부터 올라간다) — 고른 순서대로 칩이 서고, 그 순서로 보낸다.
 * ★ 이전 요청은 **끊는다**(AbortController). 빨리 칠수록 응답이 뒤엉켜 옛 결과가 나중에 오는 일을 막는다.
 */

export interface PickedUser {
  userId: string;
  name: string;
  position: string | null;
}

interface Props {
  label: string;
  hint?: string;
  value: PickedUser[];
  onChange: (next: PickedUser[]) => void;
  /** 이미 다른 칸에 담긴 사람 — 목록에서 감춘다(같은 사람이 두 줄로 가면 결재함에 두 번 뜬다). */
  exclude?: string[];
  inputId?: string;
  /**
   * 최대 인원 (2026-09-04 · 판 P1). 정본 B11-3 이 **결재자 0~3 · 최종결재자 1** 로 못 박았다(이름은 2026-09-24 판).
   *
   * ★ 다 차면 **찾는 칸을 감추고 그 사실을 적는다** — 고를 수 있는데 안 담기는 칸이 제일 나쁘다.
   * ★ `1` 이면 **새로 고른 사람이 앞 사람을 대신한다**(빼고 다시 고르게 하지 않는다).
   * ★ 화면만 막으면 API 로 우회되므로 **서버도 같은 상한을 본다**(`checkLineSize`).
   */
  max?: number;
}

export function ApproverPicker({ label, hint, value, onChange, exclude = [], inputId, max }: Props): JSX.Element {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserSummary[]>([]);
  const [open, setOpen] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (q.length === 0) { setResults([]); return; }
    const timer = setTimeout(() => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      searchUsers(q, 10, ac.signal)
        .then((users) => { setResults(users); setOpen(true); })
        .catch(() => { /* 취소·오류 — 목록만 비운다 */ });
    }, 200);
    return () => clearTimeout(timer);
  }, [query]);

  const taken = new Set([...value.map((v) => v.userId), ...exclude]);
  const visible = results.filter((u) => !taken.has(u.id));

  const full = typeof max === 'number' && value.length >= max;

  const add = (u: UserSummary) => {
    const picked = { userId: u.id, name: u.name, position: u.position };
    // 한 명만 담는 칸은 **갈아 끼운다** — 빼고 다시 고르게 하면 한 걸음이 는다.
    onChange(max === 1 ? [picked] : [...value, picked]);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="approval-picker">
      <span className="approval-picker__label">
        {label}
        {hint && <small> {hint}</small>}
      </span>

      {value.length > 0 && (
        <Chips className="approval-picker__chips">
          {value.map((u, i) => (
            /* 정본 D14 — 순서가 곧 결재 단계라 앞 슬롯에 번호가 붙는다. */
            <Chip
              key={u.userId}
              lead={i + 1}
              onRemove={() => onChange(value.filter((v) => v.userId !== u.userId))}
              removeLabel={`${u.name} 빼기`}
            >
              {u.name}{u.position ? ` · ${u.position}` : ''}
            </Chip>
          ))}
        </Chips>
      )}

      {full && max !== 1 && (
        <p className="approval-picker__empty">
          {max}명까지 담을 수 있습니다 — 바꾸려면 먼저 한 사람을 빼세요.
        </p>
      )}

      {!full || max === 1 ? (
      <SearchInput
        id={inputId}
        fullWidth
        value={query}
        placeholder="이름으로 찾기"
        onChange={(e) => setQuery(e.target.value)}
        onClear={() => setQuery('')}
        onFocus={() => { if (results.length > 0) setOpen(true); }}
        /* ★ 후보를 «누르기 전에» 닫으면 고를 수가 없다 — 종전 지연을 그대로 둔다. */
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        aria-label={label}
      />
      ) : null}

      {open && visible.length > 0 && (
        <ul className="approval-picker__results" role="listbox" aria-label={`${label} 후보`}>
          {visible.map((u) => (
            <li key={u.id}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => add(u)}>
                {u.name}<small>{u.position ? ` · ${u.position}` : ''}{u.email ? ` · ${u.email}` : ''}</small>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && query.trim().length > 0 && visible.length === 0 && (
        <p className="approval-picker__empty">찾는 사람이 없습니다.</p>
      )}
    </div>
  );
}
